import type { ReactNode } from 'react';
import type { ShopifyGlance } from '@discovr/types';
import { Skeleton } from '../components/ui/skeleton';
import { formatStoreMoney } from '../campaign-detail/components/shopifyCurrency';

export type ShopifyGlanceKey = 'campaigns' | 'live_codes' | 'sales' | 'creators_without_code';

export interface ShopifyGlanceRowProps {
  glance?: ShopifyGlance;
  loading?: boolean;
  /** Wraps a tile in the app's router link; keeps this component router-free.
   *  Returns null when the figure has no page of its own, and the tile renders as text. */
  renderLink: (key: ShopifyGlanceKey, className: string, children: ReactNode) => ReactNode;
}

const intFmt = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

const STATIC_TILE_CLASS =
  'border-border bg-card flex h-full w-full min-w-0 flex-col rounded-xl border p-3';
const LINK_ONLY_CLASS =
  'hover:bg-muted focus-visible:ring-ring cursor-pointer transition-colors focus-visible:ring-2 focus-visible:outline-none';
const TILE_CLASS = `${STATIC_TILE_CLASS} ${LINK_ONLY_CLASS}`;

/** One compact row of four store figures, each a link where it has a page. */
export function ShopifyGlanceRow({ glance, loading, renderLink }: ShopifyGlanceRowProps) {
  const grid = 'grid min-w-0 grid-cols-2 gap-2';
  if (loading) {
    return (
      <div className={grid} data-testid="shopify-glance-loading" aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-[76px] rounded-xl" />
        ))}
      </div>
    );
  }
  if (!glance) return null;
  const tiles: Array<{ key: ShopifyGlanceKey; label: string; value: string }> = [
    { key: 'campaigns', label: 'Campaigns using Shopify', value: intFmt.format(glance.campaigns) },
    { key: 'live_codes', label: 'Live discount codes', value: intFmt.format(glance.live_codes) },
    {
      key: 'sales',
      label: 'Net sales',
      value: formatStoreMoney(glance.net_sales, glance.currency),
    },
    {
      key: 'creators_without_code',
      label: 'Creators without a code',
      value: intFmt.format(glance.creators_without_code),
    },
  ];
  return (
    <div className={grid} data-testid="shopify-glance">
      {tiles.map((t) => {
        const content = (
          <>
            <span className="text-muted-foreground text-caption-medium uppercase tracking-wide">
              {t.label}
            </span>
            <span className="text-foreground mt-auto min-w-0 wrap-break-word pt-1 text-heading-h6 tracking-tight">
              {t.value}
            </span>
          </>
        );
        return (
          <div key={t.key} className="flex min-w-0">
            {renderLink(t.key, TILE_CLASS, content) ?? (
              <div className={STATIC_TILE_CLASS}>{content}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
